// Custom hook for managing the pairing flow

import { useState, useCallback, useEffect } from 'react';
import { pairingService } from '../services/pairingService';
import { usePairingToken } from './usePairingToken';

const PAIRING_STATES = {
  INITIAL: 'INITIAL',
  CHECKING_TOKEN: 'CHECKING_TOKEN',
  NO_TOKEN: 'NO_TOKEN',
  ENTERING_CODE: 'ENTERING_CODE',
  VALIDATING_CODE: 'VALIDATING_CODE',
  PAIRING_SUCCESS: 'PAIRING_SUCCESS',
  PAIRING_ERROR: 'PAIRING_ERROR',
  TOKEN_READY: 'TOKEN_READY'
};

export const usePairingFlow = () => {
  const { token, isValid, isChecking, saveToken, clearToken, daysUntilExpiry } = usePairingToken();
  const [pairingState, setPairingState] = useState(PAIRING_STATES.INITIAL);
  const [pairingCode, setPairingCode] = useState('');
  const [error, setError] = useState(null);

  // Determine initial state based on token
  useEffect(() => {
    if (isChecking) {
      setPairingState(PAIRING_STATES.CHECKING_TOKEN);
    } else if (isValid && token) {
      setPairingState(PAIRING_STATES.TOKEN_READY);
    } else {
      setPairingState(PAIRING_STATES.NO_TOKEN);
    }
  }, [isChecking, isValid, token]);

  const updatePairingCode = useCallback((code) => {
    // Only allow digits, max 6 characters
    const filtered = code.replace(/\D/g, '').slice(0, 6);
    setPairingCode(filtered);
    setError(null);
  }, []);

  const submitPairingCode = useCallback(async () => {
    if (pairingCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setPairingState(PAIRING_STATES.VALIDATING_CODE);
    setError(null);

    const result = await pairingService.pairDevice(pairingCode);

    if (result.success) {
      // Save token to localStorage
      const saved = saveToken(result.data.pairToken, result.data.user?._id);

      if (saved) {
        setPairingState(PAIRING_STATES.PAIRING_SUCCESS);

        // Transition to ready state after brief delay
        setTimeout(() => {
          setPairingState(PAIRING_STATES.TOKEN_READY);
        }, 1500);
      } else {
        setPairingState(PAIRING_STATES.PAIRING_ERROR);
        setError('Failed to save token');
      }
    } else {
      setPairingState(PAIRING_STATES.PAIRING_ERROR);
      setError(result.error);

      // Reset to entering code after delay
      setTimeout(() => {
        setPairingState(PAIRING_STATES.ENTERING_CODE);
        setPairingCode('');
      }, 3000);
    }
  }, [pairingCode, saveToken]);

  const retryPairing = useCallback(() => {
    setPairingCode('');
    setError(null);
    setPairingState(PAIRING_STATES.NO_TOKEN);
  }, []);

  const clearAndRestart = useCallback(() => {
    clearToken();
    setPairingCode('');
    setError(null);
    setPairingState(PAIRING_STATES.NO_TOKEN);
  }, [clearToken]);

  return {
    pairingState,
    pairingCode,
    token,
    error,
    daysUntilExpiry,
    isTokenReady: pairingState === PAIRING_STATES.TOKEN_READY,
    updatePairingCode,
    submitPairingCode,
    retryPairing,
    clearAndRestart
  };
};
